import { APP_ENV, APP_VERSION } from "#next/appEnv";
import { DefaultLayout } from "#next/components/base/DefaultLayout";
import { LoadFeatureAccess } from "#next/components/base/FeatureAccess";
import createEmotionCache from "#next/createEmotionCache";
import { reduxStore } from "#next/redux/reduxStore";
import theme from "#next/theme";
import { trpc } from "#next/utils/trpc";
import {
  UserProvider,
  useUser,
  withPageAuthRequired,
} from "@auth0/nextjs-auth0/client";
import { datadogLogs } from "@datadog/browser-logs";
import { datadogRum } from "@datadog/browser-rum";
import { CacheProvider, EmotionCache } from "@emotion/react";
import { CircularProgress } from "@mui/material";
import CssBaseline from "@mui/material/CssBaseline";
import { ThemeProvider } from "@mui/material/styles";
import { LicenseInfo } from "@mui/x-license-pro";
import { ConfirmProvider } from "material-ui-confirm";
import { AppProps } from "next/app";
import Head from "next/head";
import { useRouter } from "next/router";
import { ReactElement, StrictMode, useState } from "react";
import { Provider as ReduxProvider } from "react-redux";
import "#next/components/explorer/visuals/styles.css";

LicenseInfo.setLicenseKey(
  "f4b258d5bf653156077748f84b923f03Tz01MDg0NyxFPTE2OTQ3ODI3MjE4NjYsUz1wcm8sTE09c3Vic2NyaXB0aW9uLEtWPTI=",
);

const enableDatadog = APP_ENV === "production" || APP_ENV === "pre-release";

if (enableDatadog) {
  // TODO: UNDO
  const clientToken = "pubc23c1f4f7c05ba531431d2412e5b45fe";
  const applicationId = "67017dab-6e98-42fa-9106-2563f9caa4af";
  const site = "datadoghq.com";
  const service = "main-web-app";
  datadogRum.init({
    applicationId,
    clientToken,
    site,
    service,
    env: APP_ENV,
    // Specify a version number to identify the deployed version of your application in Datadog
    version: APP_VERSION,
    sessionSampleRate: 100,
    sessionReplaySampleRate: 100,
    trackUserInteractions: true,
    trackResources: true,
    trackLongTasks: true,
    defaultPrivacyLevel: "allow",
  });
  if (APP_ENV === "production") {
    datadogRum.startSessionReplayRecording();
  }
  datadogLogs.init({
    clientToken,
    site,
    forwardErrorsToLogs: true,
    sessionSampleRate: 100,
    env: APP_ENV,
    service,
    version: APP_VERSION,
    forwardConsoleLogs: ["error", "warn"],
  });
} else {
  console.warn(
    `Datadog not enabled. If this app is production or pre-release then that is a bug. APP_ENV=${APP_ENV}`,
  );
}

const clientSideEmotionCache = createEmotionCache();

interface MyAppProps extends AppProps {
  emotionCache?: EmotionCache;
  allowAnonymousAccess?: boolean;
}

const EnrichDatadogContext = () => {
  const [isContextEnriched, setIsContextEnriched] = useState(false);
  const userQuery = trpc.user.me.useQuery(undefined, {
    trpc: { context: { skipBatch: true } },
  });

  if (userQuery.isSuccess && !isContextEnriched) {
    const {
      id,
      name,
      organization: { id: orgId, name: orgName },
    } = userQuery.data;
    datadogRum.setUser({ id: id, name, orgId, orgName });

    setIsContextEnriched(true);
  }
  return null;
};

const MyApp = (props: MyAppProps) => {
  const { Component, emotionCache = clientSideEmotionCache, pageProps } = props;
  const { allowAnonymousAccess } = pageProps;

  const Page = () => (
    <>
      {enableDatadog && <EnrichDatadogContext />}
      <Component {...pageProps} />
    </>
  );

  return (
    <StrictMode>
      <ReduxProvider store={reduxStore}>
        <UserProvider>
          <CacheProvider value={emotionCache}>
            {allowAnonymousAccess ? (
              <AnonymousContent>
                <Page />
              </AnonymousContent>
            ) : (
              <AuthRequiredPage>
                <Page />
              </AuthRequiredPage>
            )}
          </CacheProvider>
        </UserProvider>
      </ReduxProvider>
    </StrictMode>
  );
};

const Content = ({ children }: { children: ReactElement }) => {
  const { user, error, isLoading } = useUser();
  const router = useRouter();
  if (isLoading) return <CircularProgress />;
  if (error) return <div>{error.message}</div>;
  if (user === undefined) {
    router.push("/api/auth/login");
    return null;
  }

  return (
    <>
      <Head>
        <meta name="viewport" content="initial-scale=1, width=device-width" />
        <link rel="shortcut icon" href="/favicon.ico" />
      </Head>
      <ThemeProvider theme={theme}>
        <CssBaseline enableColorScheme />
        <LoadFeatureAccess>
          <ConfirmProvider>
            <DefaultLayout>{children}</DefaultLayout>
          </ConfirmProvider>
        </LoadFeatureAccess>
      </ThemeProvider>
    </>
  );
};

// The only real difference right now when allowing AnonymousContent is that we don't care if user is logged in
// We still need to check and determine whether to LoadFeatureAccess, but don't route to login if no user is found
const AnonymousContent = ({ children }: { children: ReactElement }) => {
  const { user, error, isLoading } = useUser();
  if (isLoading) return <CircularProgress />;
  if (error) return <div>{error.message}</div>;

  return (
    <>
      <Head>
        <meta name="viewport" content="initial-scale=1, width=device-width" />
        <link rel="shortcut icon" href="/favicon.ico" />
      </Head>
      <ThemeProvider theme={theme}>
        <CssBaseline enableColorScheme />
        {user ? (
          <LoadFeatureAccess>
            <ConfirmProvider>
              <DefaultLayout>{children}</DefaultLayout>
            </ConfirmProvider>
          </LoadFeatureAccess>
        ) : (
          <ConfirmProvider>
            <DefaultLayout>{children}</DefaultLayout>
          </ConfirmProvider>
        )}
      </ThemeProvider>
    </>
  );
};

const AuthRequiredPage = withPageAuthRequired(Content);

// function getBaseUrl() {
//   if (typeof window !== "undefined") {
//     return "";
//   }
//   // assume localhost
//   return `http://localhost:${process.env.PORT ?? 3000}`;
// }

const AppWithTRPC = trpc.withTRPC(MyApp);

// const MyAppWrapped = withTRPC<AppRouter>({
//   // eslint-disable-next-line @typescript-eslint/no-unused-vars
//   config() {
//     /**
//      * If you want to use SSR, you need to use the server's full URL
//      * @link https://trpc.io/docs/ssr
//      */
//     return {
//       /**
//        * @link https://trpc.io/docs/links
//        */
//       links: [
//         // adds pretty logs to your console in development and logs errors in production
//         loggerLink({
//           enabled: (opts) =>
//             APP_ENV !== "production" ||
//             (opts.direction === "down" && opts.result instanceof Error),
//         }),
//         httpBatchLink({
//           url: `${getBaseUrl()}/api/trpc`,
//         }),
//       ],
//       /**
//        * @link https://trpc.io/docs/data-transformers
//        */
//       transformer: superjson,
//       /**
//        * @link https://react-query.tanstack.com/reference/QueryClient
//        */
//       // Globally disable refetchOnWindowFocus because it leads to surprising and confusing
//       // behaviour, especially regarding search builder and Redux store state.
//       queryClientConfig: {
//         defaultOptions: { queries: { refetchOnWindowFocus: false } },
//       },
//     };
//   },
//   /**
//    * @link https://trpc.io/docs/ssr
//    */
//   ssr: true,
//   /**
//    * Set headers or status code when doing SSR
//    */
//   responseMeta(opts) {
//     const ctx = opts.ctx as SSRContext;

//     if (ctx.status) {
//       // If HTTP status set, propagate that
//       return {
//         status: ctx.status,
//       };
//     }

//     const error = opts.clientErrors[0];
//     if (error) {
//       // Propagate http first error from API calls
//       return {
//         status: error.data?.httpStatus ?? 500,
//       };
//     }
//     // For app caching with SSR see https://trpc.io/docs/caching
//     return {};
//   },
// })(MyApp);

export default AppWithTRPC;
